import React from "react";
import "./AboutPage.css";

import {
  UnauthorisedPageSurface,
  PageWrapper,
} from "../../components/layout/container/Container";

const AboutPage = () => {
  document.title = "Katch | About";

  const features = [
    {
      title: "Live Sports Streaming",
      desc: "Broadcast community games live for families, supporters and fans.",
      tag: "Live",
    },
    {
      title: "Full Game Replays",
      desc: "Keep every game available for players and supporters to watch again.",
      tag: "Replay",
    },
    {
      title: "Public Sharing",
      desc: "Publish streams, replays and photo albums for the wider community.",
      tag: "Public",
    },
    {
      title: "Private Groups",
      desc: "Share selected content securely with approved teams, families and members.",
      tag: "Private",
    },
  ];

  return (
    <UnauthorisedPageSurface>
      <PageWrapper className="about-page__wrapper">
        <header className="about-page__hero">
          <div className="about-page__container">
            <span className="about-page__tagline">Community Sports</span>

            <h1 className="about-page__title">
              Host Games. Stream Live. Share Replays.
            </h1>

            <p className="about-page__description">
              Katch gives clubs, teams and sports creators one place to stream
              games live, share full replays and publish photos.
            </p>

            <p>
              Content can be shared publicly with the wider community or
              privately with approved groups, players, families and supporters.
            </p>
          </div>
        </header>

        <section className="about-page__container about-page__core-block">
          <div className="about-page__core-header">
            <span className="about-page__core-label">Our Purpose</span>

            <h2 className="about-page__core-title">
              Bringing Community Sports Closer
            </h2>
          </div>

          <div className="about-page__core-content">
            <p className="about-page__core-lead">
              Community games deserve to be seen, remembered and shared. Katch
              makes it easier for clubs and creators to capture game day and
              connect supporters with the teams they care about.
            </p>

            <div className="about-page__value-strip">
              <div className="about-page__value-item">
                <span className="about-page__value-tag">Host</span>

                <p>
                  Create a home for your games, teams, streams, replays and
                  photos.
                </p>
              </div>

              <div className="about-page__value-item">
                <span className="about-page__value-tag">Stream</span>

                <p>
                  Broadcast games live so supporters can watch from anywhere.
                </p>
              </div>

              <div className="about-page__value-item">
                <span className="about-page__value-tag">Share</span>

                <p>
                  Publish replays and photos publicly or within private groups.
                </p>
              </div>
            </div>

            <div className="about-page__core-statement">
              <p>
                From the first whistle to the final replay, Katch keeps your
                community connected to every game and every moment.
              </p>
            </div>
          </div>
        </section>

        <section className="about-page__container about-page__core-block">
          <div className="about-page__core-header">
            <span className="about-page__core-label">How It Works</span>

            <h2 className="about-page__core-title">
              Everything Your Game Needs
            </h2>
          </div>

          <div className="about-page__grid-2x2">
            <div className="about-page__work-card">
              <span className="about-page__work-number">01</span>

              <h4>Host Your Games</h4>

              <p>
                Create and organise games for your club, team or sports
                community.
              </p>
            </div>

            <div className="about-page__work-card">
              <span className="about-page__work-number">02</span>

              <h4>Stream Live</h4>

              <p>
                Broadcast games live and give supporters a simple place to
                watch.
              </p>
            </div>

            <div className="about-page__work-card">
              <span className="about-page__work-number">03</span>

              <h4>Share Replays</h4>

              <p>
                Keep full-game footage available for players, families, coaches
                and fans.
              </p>
            </div>

            <div className="about-page__work-card">
              <span className="about-page__work-number">04</span>

              <h4>Upload Photos</h4>

              <p>
                Create albums featuring players, teams and memorable game-day
                moments.
              </p>
            </div>
          </div>
        </section>

        <section className="about-page__container about-page__core-block">
          <div className="about-page__core-header">
            <span className="about-page__core-label">Flexible Access</span>

            <h2 className="about-page__core-title">
              Share Publicly or Privately
            </h2>
          </div>

          <div className="about-page__stakeholder-grid">
            <div className="about-page__stakeholder-card">
              <h4>Public Content</h4>

              <p>
                Publish live streams, replays and photo albums for anyone in the
                community to discover and watch.
              </p>
            </div>

            <div className="about-page__stakeholder-card">
              <h4>Private Groups</h4>

              <p>
                Create private spaces where only approved members can access
                selected games, videos and photos.
              </p>
            </div>

            <div className="about-page__stakeholder-card">
              <h4>Controlled Membership</h4>

              <p>
                Review join requests and decide who can access your private
                community content.
              </p>
            </div>

            <div className="about-page__stakeholder-card">
              <h4>One Media Library</h4>

              <p>
                Keep streams, replays and photo albums organised in one place.
              </p>
            </div>
          </div>
        </section>

        <section className="about-page__container about-page__core-block">
          <div className="about-page__core-header">
            <span className="about-page__core-label">Our Community</span>

            <h2 className="about-page__core-title">Who Katch Is For</h2>
          </div>

          <div className="about-page__stakeholder-grid">
            <div className="about-page__stakeholder-card">
              <h4>Clubs</h4>

              <p>
                Build a central home for club games, broadcasts, replays and
                media.
              </p>
            </div>

            <div className="about-page__stakeholder-card">
              <h4>Teams</h4>

              <p>
                Keep players and families connected to every match and shared
                moment.
              </p>
            </div>

            <div className="about-page__stakeholder-card">
              <h4>Sports Creators</h4>

              <p>Host and publish the games, videos and photos you capture.</p>
            </div>

            <div className="about-page__stakeholder-card">
              <h4>Players and Families</h4>

              <p>
                Watch live, revisit full games and keep memories from the
                season.
              </p>
            </div>

            <div className="about-page__stakeholder-card about-page__stakeholder-card--full-width">
              <h4>Supporters and Fans</h4>

              <p>
                Follow community teams, watch games and stay connected wherever
                you are.
              </p>
            </div>
          </div>
        </section>

        <section className="about-page__container about-page__promise-section">
          <h2 className="about-page__promise-title">Our Promise</h2>

          <div className="about-page__promise-grid">
            <div className="about-page__promise-item">
              <span className="about-page__promise-accent">
                Simple Streaming
              </span>

              <p>Make community games easier to host, stream and watch.</p>
            </div>

            <div className="about-page__promise-item">
              <span className="about-page__promise-accent">
                Flexible Sharing
              </span>

              <p>
                Give creators control over what is public and what stays
                private.
              </p>
            </div>

            <div className="about-page__promise-item">
              <span className="about-page__promise-accent">
                Lasting Replays
              </span>

              <p>
                Keep the games and moments that matter available beyond game
                day.
              </p>
            </div>
          </div>

          <div className="about-page__promise-quote">
            <p>
              Katch Sports is where community games are hosted, streamed and
              shared.
            </p>
          </div>
        </section>

        <section className="about-page__container about-page__core-block">
          <div className="about-page__core-header">
            <span className="about-page__core-label">The Platform</span>

            <h2 className="about-page__core-title">
              Built for Community Game Day
            </h2>
          </div>

          <div className="about-page__features-list">
            {features.map((feature) => (
              <div key={feature.title} className="about-page__feature-row">
                <div className="about-page__feature-info">
                  <span className="about-page__feature-category">
                    {feature.tag}
                  </span>

                  <h4 className="about-page__feature-name">{feature.title}</h4>

                  <p className="about-page__feature-desc">{feature.desc}</p>
                </div>

                <div className="about-page__feature-action">
                  <div className="about-page__feature-indicator" />
                </div>
              </div>
            ))}
          </div>
        </section>

        <footer className="about-page__footer">
          <div className="about-page__footer-meta">
            <p>
              &copy; 2026 Katch Sports. Community games, live and on demand.
            </p>
          </div>
        </footer>
      </PageWrapper>
    </UnauthorisedPageSurface>
  );
};

export default AboutPage;
